import { useState } from 'react';
import { Search, Moon, Sun, Send } from 'lucide-react';

const toolsData = [
  // ----- Existing and new tools -----
  { name: 'SpeedTest', category: 'Network', description: 'Test your internet speed online.', link: 'https://www.speedtest.net' },
  { name: 'Postman', category: 'API', description: 'Design, test, and document APIs easily.', link: 'https://www.postman.com' },
  { name: 'Ping Tool', category: 'Networking', description: 'Check latency and connectivity to servers.', link: 'https://ping.canbeuseful.com' },
  { name: 'JSON Formatter', category: 'Development', description: 'Format and validate JSON data.', link: 'https://jsonformatter.curiousconcept.com/' },
  { name: 'DNS Lookup', category: 'Networking', description: 'Check DNS records for a domain.', link: 'https://dnschecker.org/' },
  { name: 'Regex Tester', category: 'Development', description: 'Test and debug your regular expressions.', link: 'https://regex101.com/' },
  { name: 'UUID Generator', category: 'Utilities', description: 'Generate universally unique identifiers.', link: 'https://www.uuidgenerator.net/' },
  { name: 'Base64 Encoder/Decoder', category: 'Utilities', description: 'Encode and decode Base64 strings.', link: 'https://www.base64decode.org/' },
  { name: 'Image Compressor', category: 'Media', description: 'Compress images without losing quality.', link: 'https://tinypng.com/' },
  { name: 'Color Picker', category: 'Design', description: 'Choose and explore colors for web design.', link: 'https://coolors.co/' },
  { name: 'Markdown Editor', category: 'Development', description: 'Write and preview Markdown documents online.', link: 'https://stackedit.io/app' },
  { name: 'Website Uptime Checker', category: 'Networking', description: 'Check if a website is up or down.', link: 'https://downforeveryoneorjustme.com/' },
  { name: 'QR Code Generator', category: 'Utilities', description: 'Generate custom QR codes easily.', link: 'https://www.qr-code-generator.com/' },
  { name: 'Password Generator', category: 'Security', description: 'Generate strong random passwords.', link: 'https://passwordsgenerator.net/' },
  { name: 'SSL Checker', category: 'Security', description: 'Verify SSL certificate installation.', link: 'https://www.sslshopper.com/ssl-checker.html' },
  // ----- Oracle Tools -----
  { name: 'Oracle Live SQL', category: 'Oracle', description: 'Run SQL scripts online with Oracle Database.', link: 'https://livesql.oracle.com/' },
  { name: 'Oracle Apex', category: 'Oracle', description: 'Develop web applications using Oracle APEX online.', link: 'https://apex.oracle.com/' },
  { name: 'Oracle Cloud Free Tier', category: 'Oracle', description: 'Access free Oracle Cloud services and resources.', link: 'https://www.oracle.com/cloud/free/' },
  // ----- AWS Tools -----
  { name: 'AWS Free Tier', category: 'AWS', description: 'Try AWS services free for 12 months.', link: 'https://aws.amazon.com/free/' },
  { name: 'AWS Pricing Calculator', category: 'AWS', description: 'Estimate AWS service costs online.', link: 'https://calculator.aws.amazon.com/' },
  { name: 'AWS Well-Architected Tool', category: 'AWS', description: 'Assess workloads against AWS best practices.', link: 'https://aws.amazon.com/well-architected-tool/' },
  // ----- Azure Tools -----
  { name: 'Azure Free Account', category: 'Azure', description: 'Get free access to popular Azure services.', link: 'https://azure.microsoft.com/en-us/free/' },
  { name: 'Azure Pricing Calculator', category: 'Azure', description: 'Estimate costs for Azure services.', link: 'https://azure.microsoft.com/en-us/pricing/calculator/' },
  { name: 'Azure Cloud Shell', category: 'Azure', description: 'Manage Azure resources from browser-based shell.', link: 'https://shell.azure.com/' },
  // ----- Networking Tools -----
  { name: 'Pingdom Tools', category: 'Networking', description: 'Check website performance and uptime online.', link: 'https://www.pingdom.com/tools/' },
  { name: 'MX Toolbox', category: 'Networking', description: 'Check DNS, blacklists, SMTP and network health.', link: 'https://mxtoolbox.com/' },
  { name: 'IP Location Finder', category: 'Networking', description: 'Identify the geographic location of IP addresses.', link: 'https://www.iplocation.net/' },
  // ----- DevSecOps Tools -----
  { name: 'OWASP ZAP', category: 'DevSecOps', description: 'Open-source web application security scanner.', link: 'https://www.zaproxy.org/' },
  { name: 'Snyk Free', category: 'DevSecOps', description: 'Find and fix vulnerabilities in code and dependencies.', link: 'https://snyk.io/' },
  { name: 'Dependency-Check', category: 'DevSecOps', description: 'Detect vulnerable dependencies in projects.', link: 'https://owasp.org/www-project-dependency-check/' },
  // ----- Testing Tools -----
  { name: 'TestCafe', category: 'Testing', description: 'Automated end-to-end testing tool for web apps.', link: 'https://devexpress.github.io/testcafe/' },
  { name: 'BrowserStack Free Trial', category: 'Testing', description: 'Cross-browser testing platform.', link: 'https://www.browserstack.com/' },
  { name: 'JMeter', category: 'Testing', description: 'Open-source load and performance testing tool.', link: 'https://jmeter.apache.org/' },
];

export default function App() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [darkMode, setDarkMode] = useState(false);
  const [formData, setFormData] = useState({ name: '', category: '', description: '', link: '' });

  const categories = ['All', 'Network', 'API', 'Development', 'Utilities', 'Security', 'Design', 'Media', 'Oracle', 'AWS', 'Azure', 'DevSecOps', 'Testing'];

  const filteredTools = toolsData.filter(
    tool => (filter === 'All' || tool.category === filter) &&
            tool.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleFormSubmit = (e) => {
    e.preventDefault();
    alert(`Thank you for submitting your tool suggestion, ${formData.name || 'Contributor'}!`);
    setFormData({ name: '', category: '', description: '', link: '' });
  };

  return (
    <div className={`${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-800'} min-h-screen p-8 transition-colors duration-300`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Cosmicro IT - Resource Tools Library</h1>
          <button onClick={() => setDarkMode(!darkMode)} className="flex items-center gap-2 border p-2 rounded">
            {darkMode ? <Sun size={18}/> : <Moon size={18}/>}
            {darkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>

        <p className="text-center mb-8 text-gray-500 dark:text-gray-400">
          Discover and use essential online tools for development, cloud, networking, and DevSecOps.
        </p>

        {/* Search + Filter */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-8">
          <div className="relative w-full md:w-1/2">
            <Search className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search tools..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-10 w-full p-2 border rounded ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-100' : ''}`}
            />
          </div>
          <div className="flex gap-2 flex-wrap justify-center">
            {categories.map(cat => (
              <button key={cat} className={`px-4 py-2 rounded-full ${filter===cat ? 'bg-blue-600 text-white' : 'border'}`} onClick={()=>setFilter(cat)}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Tool Cards */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-12">
          {filteredTools.map(tool => (
            <div key={tool.name} className={`p-4 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'} hover:shadow-lg transition`}>
              <h2 className="font-semibold text-lg mb-2">{tool.name}</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">{tool.description}</p>
              <a href={tool.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
                Visit Tool →
              </a>
            </div>
          ))}
        </div>

        {/* Submit Tool Form */}
        <div className={`rounded-2xl shadow-md p-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
          <h2 className="text-2xl font-bold mb-4 text-center">Submit Your Tool</h2>
          <p className="text-center text-gray-500 dark:text-gray-400 mb-6">
            Suggest a free online tool to be added to our library.
          </p>
          <form onSubmit={handleFormSubmit} className="grid gap-4 md:grid-cols-2">
            <input placeholder="Tool Name" value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} required className={`p-2 border rounded ${darkMode?'bg-gray-700 border-gray-600 text-gray-100':''}`} />
            <input placeholder="Category" value={formData.category} onChange={e=>setFormData({...formData, category:e.target.value})} required className={`p-2 border rounded ${darkMode?'bg-gray-700 border-gray-600 text-gray-100':''}`} />
            <textarea placeholder="Description" value={formData.description} onChange={e=>setFormData({...formData, description:e.target.value})} required className={`md:col-span-2 p-2 border rounded ${darkMode?'bg-gray-700 border-gray-600 text-gray-100':''}`} />
            <input placeholder="Tool Link" type="url" value={formData.link} onChange={e=>setFormData({...formData, link:e.target.value})} required className={`md:col-span-2 p-2 border rounded ${darkMode?'bg-gray-700 border-gray-600 text-gray-100':''}`} />
            <button type="submit" className="md:col-span-2 flex justify-center items-center gap-2 border px-4 py-2 rounded">
              <Send size={16}/> Submit Tool
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

